import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { supabase } from '../../lib/supabase';
import { Button } from '../../shared/ui/Button';
import { StatusBanner } from '../../shared/ui/StatusBanner';

import { AuthLayout } from './AuthLayout';
import s from './CheckInboxPage.module.css';

/** Matches the cooldown on the reset screen, and GoTrue's own send throttle. */
const RESEND_COOLDOWN_SECONDS = 45;

interface InboxState {
  email?: unknown;
}

/**
 * Where signup lands once email confirmation is on (Phase 184).
 *
 * Replaces `/verify-phone`. That gate was never a security control — the OTP is
 * always `1234` and `otp-send` returns it in the response body
 * (docs/KNOWN_ISSUES.md §11) — and confirming a real address is a stronger
 * claim than typing a code the API just handed you.
 *
 * The address arrives through router state rather than a query string on
 * purpose: an email address in a URL ends up in browser history, in the
 * Referer header of anything the page loads, and in any analytics that records
 * paths. Nothing is lost if the state is missing — the screen still explains
 * itself, it just cannot offer to resend.
 */
export function CheckInboxPage(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const email =
    typeof (location.state as InboxState | null)?.email === 'string'
      ? ((location.state as InboxState).email as string)
      : null;

  // Starts at zero, not at the cooldown. The confirmation was sent moments ago
  // during signup, and GoTrue applies its OWN per-address throttle whose
  // message names the real number of seconds — which we surface verbatim. A
  // second, invented client-side wait on arrival would only stop someone whose
  // mail genuinely did not arrive from asking again.
  const [cooldown, setCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resentAt, setResentAt] = useState<number | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    heading.current?.focus();
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => {
      setCooldown((n) => n - 1);
    }, 1000);
    return () => {
      clearTimeout(t);
    };
  }, [cooldown]);

  const onResend = useCallback(async (): Promise<void> => {
    if (email === null || cooldown > 0 || resending) return;
    setResending(true);
    setError(null);
    const { error: sendError } = await supabase.auth.resend({ type: 'signup', email });
    setResending(false);
    if (sendError !== null) {
      // GoTrue's throttle message names its own cooldown in seconds, which is
      // more useful than anything we could invent.
      setError(sendError.message);
      return;
    }
    setCooldown(RESEND_COOLDOWN_SECONDS);
    setResentAt(Date.now());
  }, [email, cooldown, resending]);

  return (
    <AuthLayout
      pitchTitle={
        <>
          One click and <em>you're in.</em>
        </>
      }
      pitchBody="Confirming your address keeps your account yours, and means we can reach you when a result is ready."
    >
      <span className={s.eyebrow}>Almost there</span>
      <h1 className={s.title} tabIndex={-1} ref={heading}>
        Check your inbox
      </h1>
      <p className={s.subtitle}>
        {email === null ? (
          <>We&apos;ve sent you a confirmation link. Open it and you&apos;re signed in.</>
        ) : (
          <>
            We&apos;ve sent a confirmation link to <strong className={s.address}>{email}</strong>.
            Open it and you&apos;re signed in.
          </>
        )}
      </p>

      <ol className={s.steps}>
        <li>Open the email titled “Confirm your email address”.</li>
        <li>Check spam or promotions if it isn&apos;t there within a minute.</li>
        <li>The link works once, and expires in about an hour.</li>
      </ol>

      {error !== null ? <StatusBanner tone="error">{error}</StatusBanner> : null}

      {/* A live region, so the outcome of a resend is announced rather than
          only appearing as a button label change. */}
      <p className={s.live} role="status">
        {resentAt !== null && error === null ? 'Confirmation email sent again.' : ''}
      </p>

      <div className={s.actions}>
        <Button
          variant="secondary"
          size="lg"
          fullWidth
          loading={resending}
          disabled={cooldown > 0 || email === null}
          onClick={() => {
            void onResend();
          }}
        >
          {cooldown > 0 ? `Send it again in ${cooldown}s` : 'Send it again'}
        </Button>

        <p className={s.footer}>
          Wrong address?{' '}
          <Button
            variant="link"
            onClick={() => {
              navigate('/signup', { replace: true });
            }}
          >
            Start again
          </Button>
        </p>
        <p className={s.footer}>
          <Link to="/login">Back to login</Link>
        </p>
      </div>
    </AuthLayout>
  );
}
